import WeakIterable               from "@/prototype/utils/WeakIterable.js";
import {json_apply_modifications} from "@/prototype/utils/JSON.js";

import {make_ids_unique} from "svg-utils";

import global from "@/prototype/global.js";

import Generic from "./Generic.js";

const d3 = global.d3;

const log = global.log.getLogger("f_mtac.scene.objects.svg.layer");
log.setLevel(log.levels.WARN);

global.make_ids_unique = make_ids_unique;

const Layer = function(name, svg) {
    var group = d3.create("svg:g").attr("class", name);
    var objects = {};

    this.layer = svg.layer_create(name);
    this.layer.classed("transform-optimized",true);

    Object.defineProperties(this,{
        ids: {
            set: () => {},
            get: () => Object.keys(objects)
        },
        empty: {
            set: () => {},
            get: () => (Object.keys(objects).length === 0)
        },
        type: {
            set: () => {},
            get: () => "svg"
        },
        group: {
            set: () => {},
            get: () => group
        },
        node: {
            set: () => {},
            get: () => group.node()
        },
        objects: {
            set: () => {},
            get: () => objects
        }
    });

    const node_delete_handler = new WeakIterable();
    const node_create_handler = new WeakIterable();
    const observer = new MutationObserver((mutations) => {
        for (let mutation of mutations) {
            let {addedNodes, removedNodes} = mutation;
            // input is of type MutationRecord
            for (let node of addedNodes) {
                for (let handler of node_create_handler) {
                    handler(node);
                }
            }
            for (let node of removedNodes) {
                for (let handler of node_delete_handler) {
                    handler(node);
                }
            }
        }
    });
    this.on = (event_name, handler) => {
        switch (event_name) {
        case "deleting":
            node_delete_handler.add(handler);
            break;
        case "creating":
            node_create_handler.add(handler);
            break;
        default:
            throw "unsupported node subscribe event";
        }
    };
    this.off = (event_name, handler) => {
        switch (event_name) {
        case "deleting":
            node_delete_handler.delete(handler);
            break;
        case "creating":
            node_create_handler.delete(handler);
            break;
        default:
            throw "unsupported node subscribe event";
        }
    };
    this.transform = ({x, y, k}) => {
        group.attr("transform", `translate(${x},${y}) scale(${k})`);
    };
    this.reset = () => {
        objects = {};
        observer.disconnect();
        this.layer.select(`g.${name}`).remove().html("");
    };
    this.publish = () => {
        this.layer.node().appendChild(group.node());
        observer.observe(group.node(), {
            attributes: false,
            childList: true,
            subtree: false
        });
    };
    this.import = (handle) => {
        var object = new Generic(group, {handle});
        objects[object.id] = object;
        return object;
    };
    this.remove = (local_id) => {
        log.debug("Layer(svg).remove", {local_id});
        if (!(local_id in objects)) {
            return;
        }
        var object = objects[local_id];
        object.handle.remove();
        delete objects[local_id];
    };
    this.create = (local_id, object_data) => {
        log.debug("Layer.create", {local_id, object_data});
        var object = new Generic(group, {id: local_id, node_data: object_data});
        objects[local_id] = object;
    };
    this.update = (local_id, object_data) => {
        log.debug("Layer(svg).update", {local_id, object_data});
        objects[local_id].deserialize(object_data);
        objects[local_id].top();
    };
    this.apply_modifications = (local_id, object_data, modifications) => {
        log.debug("Layer(svg).apply_modifications", {local_id, modifications});
        let modified = json_apply_modifications(object_data, "object_data", modifications);
        if (modified) {
            this.update(local_id, object_data);
        }
    };
    this.restore = (object) => {
        object.change_id();
        make_ids_unique(svg.root.node(), object.handle.node());
        objects[object.id] = object;
        group.node().appendChild(object.handle.node());
        return object;
    };
    this.duplicate = (local_id) => {
        log.debug("Layer(svg).duplicate", {local_id});
        var old_object = objects[local_id];
        var new_object = new Generic(group, {node_data: old_object.serialize()});
        make_ids_unique(svg.root.node(), new_object.node.node());
        objects[new_object.id] = new_object;
        return new_object;
    };

    /**
     * 
     * @returns a string with a list of svg ids
     */
    this.dump = () => {
        let str = `{"Layer": "svg", "name": ${name},`;
        for (let id in objects) {
            str += `"object": {"id": "${id}"},`;
            log.debug(`Layer(svg)[${name}].dump objects[${id}] = `, objects[id]);
        }
        str += "}";
        return str;
    };
};

export default Layer;
